import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus, User, LogOut, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useAuth } from "../../contexts/AuthContext";
import { useMe } from "../../hooks/useUser";
import { useSubdomain } from "../../hooks/useSubdomain";
import { useUIStore } from "../../stores/uiStore";
import { getAppUrl, getProfileUrl } from "../../utils/urls";

interface NavbarProps {
  onNewCommit?: () => void;
}

export default function Navbar({ onNewCommit }: NavbarProps) {
  const {
    user: authUser,
    isAuthenticated,
    isLoading: authLoading,
    signOut,
  } = useAuth();
  const { user: meUser, refetch: refetchMe } = useMe();
  const subdomain = useSubdomain();
  const location = useLocation();

  // Get store actions
  const openSettings = useUIStore((s) => s.openSettings);
  const subscribeProfile = useUIStore((s) => s.subscribeProfile);

  // Subscribe to profile updates (refetch user data after settings change)
  useEffect(() => {
    const unsubscribe = subscribeProfile(() => refetchMe());
    return unsubscribe;
  }, [subscribeProfile, refetchMe]);

  // Use cached/backend data first to prevent flickering, fall back to auth context
  const displayName = meUser?.displayName || authUser?.name;
  const avatarUrl = meUser?.avatarUrl || authUser?.picture;
  const username = meUser?.username || authUser?.username;

  // Check if we're currently on the user's own profile page
  // In production: subdomain matches username
  // In development: pathname matches /username
  const isOnOwnProfile = username && (
    subdomain === username ||
    location.pathname === `/${username}` ||
    location.pathname.startsWith(`/${username}/`)
  );

  return (
    <header
      className="sticky top-0 z-50 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]/95 backdrop-blur backdrop-saturate-200 supports-[backdrop-filter]:bg-[hsl(var(--background))]/80"
      role="banner"
    >
      <nav className="max-w-[720px] mx-auto px-4" aria-label="Main navigation">
        <div className="flex h-16 items-center justify-between">
          <Link
            to={getAppUrl()}
            className="flex items-center no-underline min-w-0 mr-2 sm:mr-4"
            aria-label="commit.you - Go to home feed"
          >
            <img
              src="/logo_dark.svg"
              alt=""
              className="h-6 sm:h-8 hidden dark:block"
              aria-hidden="true"
            />
            <img
              src="/logo_light.svg"
              alt=""
              className="h-6 sm:h-8 dark:hidden"
              aria-hidden="true"
            />
          </Link>

          <div className="flex items-center gap-2">
            {onNewCommit && (
              <Button size="sm" onClick={onNewCommit} aria-label="Create new commit">
                <Plus className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
                <span className="hidden sm:inline">New Commit</span>
              </Button>
            )}
            <ThemeToggle />
            {authLoading ? (
              // Show skeleton while auth is loading to prevent flash of "Sign In"
              <div className="h-8 w-8 rounded-full bg-[hsl(var(--muted))] animate-pulse" />
            ) : isAuthenticated ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 ring-offset-[hsl(var(--background))]"
                    aria-label="User menu"
                  >
                    <Avatar className="h-8 w-8 cursor-pointer border border-[hsl(var(--border))]">
                      <AvatarImage src={avatarUrl} alt={displayName || ""} />
                      <AvatarFallback className="text-xs">
                        {displayName?.[0]?.toUpperCase() || (
                          <User className="h-4 w-4" aria-hidden="true" />
                        )}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                      @{username || "unknown"}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  {username && !isOnOwnProfile && (
                    <DropdownMenuItem asChild>
                      <Link to={getProfileUrl(username)}>
                        <User className="mr-2 h-4 w-4" />
                        Your Profile
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={openSettings}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={signOut}
                    className="text-[hsl(var(--destructive))]"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button size="sm" asChild>
                <Link to="/login">Sign In</Link>
              </Button>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}
